import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for scroll management and positioning
 * Handles sticky controls, verse scrolling, and viewport calculations
 */
export const useScrollManagement = ({
  startVerse,
  currentVerseIndex,
  verseTextRef
}) => {
  // Scroll state
  const [isControlsSticky, setIsControlsSticky] = useState(false);

  // Scroll to top when page loads with URL parameters to ensure cassette is visible
  const scrollToTop = useCallback(() => {
    // Small delay to ensure the page has rendered
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Function to scroll to the current verse - Optimized for speed
  const scrollToCurrentVerse = useCallback((direction = 'next', verseNumber = null) => {
    if (verseTextRef.current) {
      const verseElements = verseTextRef.current.querySelectorAll('[data-verse]');
      const targetVerse = verseNumber || startVerse + currentVerseIndex;
      const currentVerseElement = Array.from(verseElements).find(el => 
        parseInt(el.getAttribute('data-verse')) === targetVerse
      );
      
      if (currentVerseElement) {
        // Get the sticky controls height to offset the scroll
        const stickyControls = document.getElementById('recording-controls');
        const offset = stickyControls && isControlsSticky ? stickyControls.offsetHeight + 80 : 80;
        
        const elementRect = currentVerseElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        if (direction === 'previous') {
          // For Previous: Only scroll if verse is getting cut off at the top
          const threshold = viewportHeight * 0.4; // Only scroll if verse is above 40% of viewport
          
          if (elementRect.top < threshold) {
            // Calculate the target scroll position
            const elementTop = currentVerseElement.offsetTop;
            const containerTop = verseTextRef.current.offsetTop;
            const targetScroll = window.pageYOffset + elementTop - containerTop - offset;
            
            // Use requestAnimationFrame for smoother, faster scrolling
            requestAnimationFrame(() => {
              window.scrollTo({
                top: targetScroll,
                behavior: 'smooth'
              });
            });
          }
        } else {
          // For Next: Only scroll if verse is getting too far down
          const threshold = viewportHeight * 0.6; // Only scroll if verse is below 60% of viewport
          
          if (elementRect.top > threshold) {
            // Calculate the target scroll position
            const elementTop = currentVerseElement.offsetTop;
            const containerTop = verseTextRef.current.offsetTop;
            const targetScroll = window.pageYOffset + elementTop - containerTop - offset;
            
            // Use requestAnimationFrame for smoother, faster scrolling
            requestAnimationFrame(() => {
              window.scrollTo({
                top: targetScroll,
                behavior: 'smooth'
              });
            });
          }
        }
      }
    }
  }, [startVerse, currentVerseIndex, verseTextRef, isControlsSticky]);

  // Scroll event listener for sticky controls - optimized for smooth scrolling
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      // Works on both mobile and desktop
      
      if (!ticking) {
        requestAnimationFrame(() => {
          const cassetteElement = document.querySelector('[data-cassette]');
          if (!cassetteElement) {
            ticking = false;
            return;
          }
          
          const cassetteRect = cassetteElement.getBoundingClientRect();
          const controlsElement = document.getElementById('recording-controls');
          
          if (!controlsElement) {
            ticking = false;
            return;
          }
          
          // If cassette is going off-screen (top is negative), make controls sticky
          const shouldBeSticky = cassetteRect.bottom < 100;
          
          // Only update state if it actually changed to prevent unnecessary re-renders
          if (shouldBeSticky !== isControlsSticky) {
            setIsControlsSticky(shouldBeSticky);
          }
          
          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isControlsSticky]);

  // Get sticky controls class name
  const getStickyControlsClass = useCallback(() => {
    // On desktop the NavBar is sticky at top-0 (h-16 = 64px), so push controls below it.
    return `transition-all duration-200 ${isControlsSticky ? 'fixed top-0 lg:top-16 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm' : ''}`;
  }, [isControlsSticky]);

  // Get controls container class name
  const getControlsContainerClass = useCallback(() => {
    return `flex gap-2 sm:gap-3 lg:gap-4 items-center justify-center ${isControlsSticky ? 'px-4 py-3' : 'mt-4 px-4 lg:px-8'}`;
  }, [isControlsSticky]);

  // Get progress bar container class name
  const getProgressBarClass = useCallback(() => {
    return `w-full grid grid-cols-4 gap-3 ${isControlsSticky ? 'px-4 mb-3' : 'mt-3 px-4 lg:px-8'}`;
  }, [isControlsSticky]);

  return {
    // State
    isControlsSticky,
    
    // Functions
    scrollToTop,
    scrollToCurrentVerse,
    
    // Class name getters
    getStickyControlsClass,
    getControlsContainerClass,
    getProgressBarClass
  };
};
